import { ActivityDto } from "@core/typesV2";
import { useService } from "@providersV2";
import { ActivityProgressService, LcmsActivityService } from "@servicesV2";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

type CourseItemActivitiesProviderProps = {
  courseId?: string;
  dayId?: string;
  learnerId?: string;
};
export function useCourseItemActivitiesProvider({
  courseId,
  dayId,
  learnerId,
}: CourseItemActivitiesProviderProps) {
  const queryClient = useQueryClient();
  const { initialization: activityProgressServiceInit } = useService(
    ActivityProgressService,
  );

  const { initialization: lcmsServiceInit } = useService(LcmsActivityService);

  return useQuery({
    enabled: !!courseId && !!dayId,
    queryKey: [
      "courses",
      courseId,
      "dayId",
      dayId,
      "learnerId",
      learnerId || null,
    ],

    async queryFn() {
      const { service: activityProgressService } =
        await activityProgressServiceInit;
      const { service: lcmsService } = await lcmsServiceInit;

      const activities = await activityProgressService.getActivities(
        z.string().parse(courseId),
        z.string().parse(dayId),
        learnerId ? z.string().parse(learnerId) : undefined,
      );

      if (activities.length === 0) return { activities, lcmsItems: [] };

      const ids = activities.map((a) => a.activityId);

      try {
        const lcmsItems = await queryClient.fetchQuery({
          queryKey: ["lcms-activities", ids],
          queryFn: () => lcmsService.getActivities(ids),
        });
        return { activities, lcmsItems };
      } catch {
        return { activities, lcmsItems: [] };
      }
    },

    select(data) {
      const { activities, lcmsItems } = data;

      if (!lcmsItems.length) return activities;

      const map = new Map(lcmsItems.map((x) => [x.id, x]));

      return activities.map((item) => {
        const lcms = map.get(item.activityId);
        if (!lcms) return item;

        return {
          ...item,
          title: lcms.title,
          description: lcms.description,
          type: lcms.type,
        } as ActivityDto;
      });
    },
  });
}
